import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Check, ChevronLeft, ChevronRight, Crown, ExternalLink, Globe, Loader2, Send, Star, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { API, wakeUpBackend } from '../lib/api';
import { useLanguage } from '../contexts/LanguageContext';

const LANDING_COPY = {
  es: {
    languageButton: 'English',
    headerCta: 'Crear mi sitio',
    heroTitle: 'La web de tu alojamiento, lista en menos de 48 horas',
    heroSubtitle: 'Un solo pago, sin cuotas mensuales. Comparte tu alojamiento con tu propia página profesional en WhatsApp, redes sociales o donde quieras y recibe consultas directas.',
    heroPrimaryCta: 'Empezar ahora',
    heroPlansCta: 'Ver planes',
    heroExampleCta: 'Ver ejemplo',
    whyTitle: '¿Por qué elegir Mi Web Vacacional?',
    whyCards: [
      {
        title: 'Pago único, sin suscripción',
        description: 'Pagas una sola vez por tu web. Sin cuotas mensuales ni permanencias.',
      },
      {
        title: 'Rápido y profesional',
        description: 'Plantilla cuidada para dar buena imagen y convertir visitas en reservas o consultas.',
      },
      {
        title: 'Publicación sencilla',
        description: 'Formulario intuitivo paso a paso. Todo sin conocimientos técnicos.',
      },
    ],
    pricingTitle: 'Elige el plan que mejor encaje contigo',
    pricingSubtitle: 'Pago único. Sin mensualidades. Sin suscripción.',
    pricingNote: 'Cambios después de publicar: 15€ por solicitud, sin importar cuántos incluyas.',
    recommended: 'Recomendado',
    oneTimePaymentSuffix: '/ pago único',
    startWith: 'Comenzar con',
    howTitle: '¿Cómo funciona?',
    howSteps: [
      {
        title: 'Completa el formulario',
        description: 'Sube fotos y añade la información principal de tu alojamiento en unos minutos.',
      },
      {
        title: 'Previsualiza tu sitio',
        description: 'Comprueba cómo quedará antes de pagar. Si algo no te encaja, realiza los ajustes necesarios y previsualiza de nuevo.',
      },
      {
        title: 'Realiza el pago',
        description: 'Pago único y seguro a través de Stripe. No es una suscripción y no hay cuotas mensuales.',
      },
      {
        title: '¡Sitio publicado!',
        description: 'Revisamos el contenido y publicamos tu web. Te enviamos un email con el enlace de tu sitio en cuanto esté activo.',
      },
    ],
    reviewsTitle: 'Opiniones de clientes',
    reviewsSubtitle: 'Historias reales de propietarios que ya tienen su web activa.',
    reviewsCta: 'Dejar mi reseña',
    reviewDialogTitle: 'Comparte tu experiencia',
    reviewDialogDescription: 'Cuéntanos cómo fue el proceso y el resultado final de tu web.',
    reviewFields: {
      reviewerName: 'Tu nombre',
      propertyName: 'Nombre del alojamiento',
      location: 'Ubicación',
      propertyUrl: 'Enlace del alojamiento (opcional)',
      email: 'Email',
      rating: 'Valoración',
      comment: 'Comentario',
      commentPlaceholder: 'Qué te gustó del proceso, de la preview o del resultado final...',
      emailPlaceholder: 'tu@email.com',
    },
    submitReview: 'Enviar reseña',
    submittingReview: 'Enviando...',
    loadingReviews: 'Cargando reseñas...',
    noReviews: 'Aún no hay reseñas publicadas. La primera puede ser la siguiente.',
    viewProperty: 'Ver alojamiento',
    previousReviews: 'Reseñas anteriores',
    nextReviews: 'Reseñas siguientes',
    reviewSubmitted: 'Gracias. Revisaremos tu reseña antes de publicarla.',
    reviewSubmitError: 'No se pudo enviar la reseña',
    rateWith: 'Valorar con',
    stars: 'estrellas',
    ctaTitle: '¿Listo para crear tu sitio web profesional?',
    ctaSubtitle: 'Lanza la web de tu alojamiento con una inversión única y sin complicaciones técnicas.',
    ctaButton: 'Crear mi sitio ahora',
    faqTitle: 'Preguntas frecuentes',
    faqItems: [
      {
        question: '¿Es un pago único o una suscripción?',
        answer: 'Es un pago único por la creación de tu web. No cobramos mensualidades por el servicio base.',
      },
      {
        question: '¿Puedo cambiar cosas después de publicar?',
        answer: 'Sí. Cualquier actualización, fotos, textos o precios, tiene un coste fijo de 15€ por solicitud, sin importar cuántos cambios incluya.',
      },
      {
        question: '¿El dominio está incluido?',
        answer: 'No. En el plan Premium conectamos tu dominio si ya lo tienes, pero el registro o compra del dominio no está incluido en el precio.',
      },
      {
        question: '¿Cuándo estará publicada mi web?',
        answer: 'Normalmente en menos de 48 horas desde el pago. Te avisamos por email en cuanto esté activa, con el enlace directo a tu sitio.',
      },
    ],
    footerRights: 'Todos los derechos reservados.',
    footerContact: 'Contacto',
    plans: {
      basic: {
        name: 'Básico',
        features: [
          'Web profesional con plantilla predefinida',
          'Galería de hasta 10 fotos',
          'Descripción del alojamiento y normas de la casa',
          'Mapa de ubicación',
          'Enlaces a Airbnb y Booking',
          'Formulario de contacto y botón de WhatsApp',
          'Previsualización antes de pagar',
        ],
      },
      plus: {
        name: 'Plus',
        features: [
          'Todo lo incluido en el plan Básico',
          'Galería de hasta 20 fotos',
          'Calendario sincronizado con Airbnb, Booking y otras plataformas',
          'Versión en inglés para llegar a clientes internacionales',
        ],
      },
      premium: {
        name: 'Premium',
        features: [
          'Todo lo incluido en el plan Plus',
          'Galería de hasta 30 fotos',
          'Conexión con tu propio dominio (no incluido en el precio)',
        ],
        disclaimer: 'La publicación con dominio propio puede tardar más de 48h.',
      },
    },
  },
  en: {
    languageButton: 'Español',
    headerCta: 'Create my site',
    heroTitle: 'Your vacation rental website ready in less than 48 hours',
    heroSubtitle: 'One-time payment, no monthly fees. Share your property with your own professional page on WhatsApp, social media, or wherever you want and receive direct inquiries.',
    heroPrimaryCta: 'Get started now',
    heroPlansCta: 'View plans',
    heroExampleCta: 'View example',
    whyTitle: 'Why choose Mi Web Vacacional?',
    whyCards: [
      {
        title: 'One-time payment, no subscription',
        description: 'You pay once for your website. No monthly fees and no lock-in.',
      },
      {
        title: 'Fast and professional',
        description: 'A polished template designed to convert visits into bookings and inquiries.',
      },
      {
        title: 'Simple publishing process',
        description: 'Step-by-step form, easy to complete without technical knowledge.',
      },
    ],
    pricingTitle: 'Choose the plan that fits you best',
    pricingSubtitle: 'One-time payment. No monthly fees. No subscription.',
    pricingNote: 'Changes after publishing: €15 per request, no matter how many updates are included.',
    recommended: 'Recommended',
    oneTimePaymentSuffix: '/ one-time payment',
    startWith: 'Start with',
    howTitle: 'How it works',
    howSteps: [
      {
        title: 'Fill out the form',
        description: 'Upload photos and add the main information about your property in just a few minutes.',
      },
      {
        title: 'Preview your site',
        description: 'Check how everything looks before paying. If something is not right, adjust it and preview again.',
      },
      {
        title: 'Complete payment',
        description: 'Secure one-time payment via Stripe. This is not a subscription and there are no monthly fees.',
      },
      {
        title: 'Website published',
        description: 'We review your content and publish your site. We send you an email with your live link as soon as it is ready.',
      },
    ],
    reviewsTitle: 'Client reviews',
    reviewsSubtitle: 'Real stories from property owners who already have their website live.',
    reviewsCta: 'Leave my review',
    reviewDialogTitle: 'Share your experience',
    reviewDialogDescription: 'Tell us how the process and the final result of your website went.',
    reviewFields: {
      reviewerName: 'Your name',
      propertyName: 'Property name',
      location: 'Location',
      propertyUrl: 'Property link (optional)',
      email: 'Email',
      rating: 'Rating',
      comment: 'Comment',
      commentPlaceholder: 'What did you like about the process, preview, or final result?',
      emailPlaceholder: 'you@email.com',
    },
    submitReview: 'Send review',
    submittingReview: 'Sending...',
    loadingReviews: 'Loading reviews...',
    noReviews: 'There are no published reviews yet. Yours could be the first one.',
    viewProperty: 'View property',
    previousReviews: 'Previous reviews',
    nextReviews: 'Next reviews',
    reviewSubmitted: 'Thanks. We will review your feedback before publishing it.',
    reviewSubmitError: 'Could not send your review',
    rateWith: 'Rate with',
    stars: 'stars',
    ctaTitle: 'Ready to build your professional website?',
    ctaSubtitle: 'Launch your property website with a one-time investment and no technical hassle.',
    ctaButton: 'Create my site now',
    faqTitle: 'Frequently asked questions',
    faqItems: [
      {
        question: 'Is it a one-time payment or a subscription?',
        answer: 'It is a one-time payment for your website setup. We do not charge monthly fees for the base service.',
      },
      {
        question: 'Can I request changes after publishing?',
        answer: 'Yes. Any update, photos, texts or prices, has a fixed cost of €15 per request, regardless of how many changes you include.',
      },
      {
        question: 'Is the domain included?',
        answer: 'No. In the Premium plan we connect your domain if you already have one, but domain purchase or registration is not included.',
      },
      {
        question: 'When will my site be published?',
        answer: 'Usually in under 48 hours after payment. We will notify you by email as soon as it is live, with your direct site link.',
      },
    ],
    footerRights: 'All rights reserved.',
    footerContact: 'Contact',
    plans: {
      basic: {
        name: 'Basic',
        features: [
          'Professional website with a predefined template',
          'Gallery with up to 10 photos',
          'Property description and house rules',
          'Location map',
          'Links to Airbnb and Booking',
          'Contact form and WhatsApp button',
          'Preview before payment',
        ],
      },
      plus: {
        name: 'Plus',
        features: [
          'Everything included in the Basic plan',
          'Gallery with up to 20 photos',
          'Calendar synced with Airbnb, Booking and other platforms',
          'English version to reach international guests',
        ],
      },
      premium: {
        name: 'Premium',
        features: [
          'Everything included in the Plus plan',
          'Gallery with up to 30 photos',
          'Connection with your own domain (not included in the price)',
        ],
        disclaimer: 'Publishing with a custom domain may take more than 48 hours.',
      },
    },
  },
};

export const LandingPage = () => {
  const { language, toggleLanguage } = useLanguage();
  const t = LANDING_COPY[language] || LANDING_COPY.es;
  const navigate = useNavigate();
  const logoSrc = '/assets/logo-mi-web-vacacional-icon.png';
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(true);
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [reviewsPage, setReviewsPage] = useState(0);
  const [reviewForm, setReviewForm] = useState({
    reviewer_name: '',
    property_name: '',
    property_url: '',
    location: '',
    contact_email: '',
    rating: 5,
    comment: '',
    website: '',
  });

  useEffect(() => {
    wakeUpBackend();
  }, []);

  const REVIEWS_PER_PAGE = 6;
  const totalReviewPages = Math.max(1, Math.ceil(reviews.length / REVIEWS_PER_PAGE));
  const visibleReviews = useMemo(() => {
    const start = reviewsPage * REVIEWS_PER_PAGE;
    return reviews.slice(start, start + REVIEWS_PER_PAGE);
  }, [reviews, reviewsPage]);

  useEffect(() => {
    if (reviewsPage > totalReviewPages - 1) {
      setReviewsPage(0);
    }
  }, [reviewsPage, totalReviewPages]);

  useEffect(() => {
    if (totalReviewPages <= 1) return;
    const interval = window.setInterval(() => {
      setReviewsPage((current) => (current + 1) % totalReviewPages);
    }, 7000);

    return () => window.clearInterval(interval);
  }, [totalReviewPages]);

  useEffect(() => {
    const loadReviews = async () => {
      try {
        setReviewsLoading(true);
        const response = await axios.get(`${API}/reviews/public?limit=12`);
        setReviews(response.data || []);
      } catch (error) {
        console.error('Error loading public reviews:', error);
      } finally {
        setReviewsLoading(false);
      }
    };

    loadReviews();
  }, []);

  const handleReviewFieldChange = (field, value) => {
    setReviewForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const handleReviewSubmit = async (event) => {
    event.preventDefault();

    try {
      setReviewSubmitting(true);
      await axios.post(`${API}/reviews`, reviewForm);
      toast.success(t.reviewSubmitted);
      setReviewForm({
        reviewer_name: '',
        property_name: '',
        property_url: '',
        location: '',
        contact_email: '',
        rating: 5,
        comment: '',
        website: '',
      });
      setReviewModalOpen(false);
    } catch (error) {
      console.error('Error submitting review:', error);
      toast.error(error.response?.data?.detail || t.reviewSubmitError);
    } finally {
      setReviewSubmitting(false);
    }
  };

  const plans = [
    {
      name: t.plans.basic.name,
      price: '49',
      icon: Zap,
      color: 'from-blue-500 to-cyan-500',
      features: t.plans.basic.features,
      planType: 'basic'
    },
    {
      name: t.plans.plus.name,
      price: '69',
      icon: Star,
      color: 'from-purple-500 to-pink-500',
      popular: true,
      features: t.plans.plus.features,
      planType: 'plus'
    },
    {
      name: t.plans.premium.name,
      price: '89',
      icon: Crown,
      color: 'from-amber-500 to-orange-500',
      features: t.plans.premium.features,
      disclaimer: t.plans.premium.disclaimer,
      planType: 'premium'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-md sticky top-0 z-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            <div className="flex items-center gap-3">
              <img
                src={logoSrc}
                alt="Mi Web Vacacional"
                className="h-10 w-10 object-contain flex-shrink-0"
              />
              <span className="hidden sm:inline text-2xl font-bold bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">
                Mi Web Vacacional
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={toggleLanguage}>
                <Globe className="h-4 w-4" />
                {language === 'es' ? 'EN' : 'ES'}
              </Button>
              <Button variant="outline" onClick={() => navigate('/submit')}>
                {t.headerCta}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto text-center max-w-4xl">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
            {t.heroTitle}
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            {t.heroSubtitle}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              onClick={() => navigate('/submit')}
              className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-lg px-8"
            >
              {t.heroPrimaryCta}
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              className="hidden sm:inline-flex"
              onClick={() => document.getElementById('pricing').scrollIntoView({ behavior: 'smooth' })}
            >
              {t.heroPlansCta}
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              className="inline-flex"
              onClick={() => navigate('/sample-property')}
            >
              {t.heroExampleCta}
            </Button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-3xl font-bold text-center mb-12">{t.whyTitle}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Zap className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2">{t.whyCards[0].title}</h3>
              <p className="text-gray-600">
                {t.whyCards[0].description}
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Star className="h-8 w-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2">{t.whyCards[1].title}</h3>
              <p className="text-gray-600">
                {t.whyCards[1].description}
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2">{t.whyCards[2].title}</h3>
              <p className="text-gray-600">
                {t.whyCards[2].description}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-4">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">{t.pricingTitle}</h2>
            <p className="text-xl text-gray-600">{t.pricingSubtitle}</p>
            <p className="text-sm text-gray-500 mt-2">{t.pricingNote}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {plans.map((plan, index) => {
              const Icon = plan.icon;
              return (
                <Card 
                  key={index}
                  className={`relative ${plan.popular ? 'border-2 border-purple-500 shadow-xl scale-105' : ''}`}
                >
                  {plan.popular && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <span className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-4 py-1 rounded-full text-sm font-semibold">
                        {t.recommended}
                      </span>
                    </div>
                  )}

                  <CardHeader>
                    <div className={`w-12 h-12 rounded-lg bg-gradient-to-r ${plan.color} flex items-center justify-center mb-4`}>
                      <Icon className="h-6 w-6 text-white" />
                    </div>
                    <CardTitle className="text-2xl">{plan.name}</CardTitle>
                    <CardDescription>
                      <span className="text-4xl font-bold text-gray-900">{plan.price}€</span>
                      <span className="text-gray-600"> {t.oneTimePaymentSuffix}</span>
                    </CardDescription>
                  </CardHeader>

                  <CardContent>
                    <ul className="space-y-3">
                      {plan.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-start gap-3">
                          <Check className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    {plan.disclaimer && (
                      <p className="text-xs text-gray-400 mt-4 border-t pt-3">{plan.disclaimer}</p>
                    )}
                  </CardContent>

                  <CardFooter>
                    <Button 
                      className="w-full"
                      variant={plan.popular ? 'default' : 'outline'}
                      onClick={() => navigate('/submit')}
                    >
                      {t.startWith} {plan.name}
                    </Button>
                  </CardFooter>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-3xl font-bold text-center mb-12">{t.howTitle}</h2>
          <div className="space-y-8">
            <div className="flex gap-6">
              <div className="flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-xl">
                1
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-2">{t.howSteps[0].title}</h3>
                <p className="text-gray-600">
                  {t.howSteps[0].description}
                </p>
              </div>
            </div>

            <div className="flex gap-6">
              <div className="flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-xl">
                2
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-2">{t.howSteps[1].title}</h3>
                <p className="text-gray-600">
                  {t.howSteps[1].description}
                </p>
              </div>
            </div>

            <div className="flex gap-6">
              <div className="flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-xl">
                3
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-2">{t.howSteps[2].title}</h3>
                <p className="text-gray-600">
                  {t.howSteps[2].description}
                </p>
              </div>
            </div>

            <div className="flex gap-6">
              <div className="flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-xl">
                4
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-2">{t.howSteps[3].title}</h3>
                <p className="text-gray-600">
                  {t.howSteps[3].description}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="mb-10 text-center max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold mb-4">{t.reviewsTitle}</h2>
            <p className="text-gray-600">{t.reviewsSubtitle}</p>
            <Dialog open={reviewModalOpen} onOpenChange={setReviewModalOpen}>
              <DialogTrigger asChild>
                <Button className="mt-5 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700">
                  <Send className="h-4 w-4" />
                  {t.reviewsCta}
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{t.reviewDialogTitle}</DialogTitle>
                  <DialogDescription>
                    {t.reviewDialogDescription}
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleReviewSubmit} className="space-y-4">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="reviewer_name">{t.reviewFields.reviewerName}</Label>
                      <Input
                        id="reviewer_name"
                        value={reviewForm.reviewer_name}
                        onChange={(event) => handleReviewFieldChange('reviewer_name', event.target.value)}
                        required
                        maxLength={120}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="property_name">{t.reviewFields.propertyName}</Label>
                      <Input
                        id="property_name"
                        value={reviewForm.property_name}
                        onChange={(event) => handleReviewFieldChange('property_name', event.target.value)}
                        required
                        maxLength={160}
                      />
                    </div>
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="location">{t.reviewFields.location}</Label>
                      <Input
                        id="location"
                        value={reviewForm.location}
                        onChange={(event) => handleReviewFieldChange('location', event.target.value)}
                        maxLength={160}
                        placeholder="Oviedo, Gijon, Llanes..."
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="property_url">{t.reviewFields.propertyUrl}</Label>
                      <Input
                        id="property_url"
                        type="url"
                        value={reviewForm.property_url}
                        onChange={(event) => handleReviewFieldChange('property_url', event.target.value)}
                        placeholder="https://..."
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="contact_email">{t.reviewFields.email}</Label>
                      <Input
                        id="contact_email"
                        type="email"
                        value={reviewForm.contact_email}
                        onChange={(event) => handleReviewFieldChange('contact_email', event.target.value)}
                        required
                        placeholder={t.reviewFields.emailPlaceholder}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>{t.reviewFields.rating}</Label>
                    <div className="flex items-center gap-2">
                      {Array.from({ length: 5 }).map((_, index) => {
                        const value = index + 1;
                        const active = value <= reviewForm.rating;
                        return (
                          <button
                            key={value}
                            type="button"
                            onClick={() => handleReviewFieldChange('rating', value)}
                            className={`rounded-full p-1 transition ${active ? 'text-amber-500' : 'text-gray-300 hover:text-amber-400'}`}
                            aria-label={`${t.rateWith} ${value} ${t.stars}`}
                          >
                            <Star className={`h-6 w-6 ${active ? 'fill-current' : ''}`} />
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="comment">{t.reviewFields.comment}</Label>
                    <Textarea
                      id="comment"
                      value={reviewForm.comment}
                      onChange={(event) => handleReviewFieldChange('comment', event.target.value)}
                      required
                      minLength={20}
                      maxLength={1200}
                      className="min-h-[140px]"
                      placeholder={t.reviewFields.commentPlaceholder}
                    />
                  </div>

                  <div className="hidden">
                    <Label htmlFor="website">Website</Label>
                    <Input
                      id="website"
                      value={reviewForm.website}
                      onChange={(event) => handleReviewFieldChange('website', event.target.value)}
                      tabIndex={-1}
                      autoComplete="off"
                    />
                  </div>

                  <Button type="submit" className="w-full" disabled={reviewSubmitting}>
                    {reviewSubmitting ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        {t.submittingReview}
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4" />
                        {t.submitReview}
                      </>
                    )}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div>
            {reviewsLoading ? (
              <div className="rounded-xl border bg-gray-50 p-8 text-center text-gray-500">
                <Loader2 className="h-6 w-6 animate-spin mx-auto mb-3" />
                {t.loadingReviews}
              </div>
            ) : reviews.length === 0 ? (
              <div className="rounded-xl border bg-gray-50 p-8 text-center text-gray-500">
                {t.noReviews}
              </div>
            ) : (
              <>
                <div className="flex flex-wrap justify-center gap-4">
                {visibleReviews.map((review) => (
                  <Card key={review.id} className="w-full max-w-md border-gray-200 shadow-sm">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-1 text-amber-500 mb-3">
                        {Array.from({ length: 5 }).map((_, index) => (
                          <Star
                            key={index}
                            className={`h-4 w-4 ${index < review.rating ? 'fill-current' : 'text-gray-300'}`}
                          />
                        ))}
                      </div>
                      <p className="text-gray-700 leading-7 mb-4">“{review.comment}”</p>
                      <div>
                        <p className="font-semibold text-gray-900">{review.reviewer_name}</p>
                        <p className="text-sm text-gray-500">{review.property_name}{review.location ? ` · ${review.location}` : ''}</p>
                        {review.property_url ? (
                          <a
                            href={review.property_url}
                            target="_blank"
                            rel="noreferrer"
                            className="mt-2 inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700"
                          >
                            <ExternalLink className="h-3.5 w-3.5" />
                            {t.viewProperty}
                          </a>
                        ) : null}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
              {totalReviewPages > 1 ? (
                <div className="mt-6 flex items-center justify-center gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => setReviewsPage((current) => (current - 1 + totalReviewPages) % totalReviewPages)}
                    aria-label={t.previousReviews}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <p className="text-sm text-gray-500">{reviewsPage + 1} / {totalReviewPages}</p>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => setReviewsPage((current) => (current + 1) % totalReviewPages)}
                    aria-label={t.nextReviews}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              ) : null}
              </>
            )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-blue-600 to-cyan-600 text-white">
        <div className="container mx-auto text-center max-w-3xl">
          <h2 className="text-4xl font-bold mb-4">
            {t.ctaTitle}
          </h2>
          <p className="text-xl mb-8 opacity-90">
            {t.ctaSubtitle}
          </p>
          <Button 
            size="lg" 
            variant="secondary"
            onClick={() => navigate('/submit')}
            className="text-lg px-8"
          >
            {t.ctaButton}
          </Button>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-3xl font-bold text-center mb-12">{t.faqTitle}</h2>
          <div className="space-y-6">
            <div className="rounded-xl border p-5 bg-gray-50">
              <h3 className="font-semibold text-lg mb-2">{t.faqItems[0].question}</h3>
              <p className="text-gray-600">{t.faqItems[0].answer}</p>
            </div>
            <div className="rounded-xl border p-5 bg-gray-50">
              <h3 className="font-semibold text-lg mb-2">{t.faqItems[1].question}</h3>
              <p className="text-gray-600">{t.faqItems[1].answer}</p>
            </div>
            <div className="rounded-xl border p-5 bg-gray-50">
              <h3 className="font-semibold text-lg mb-2">{t.faqItems[2].question}</h3>
              <p className="text-gray-600">{t.faqItems[2].answer}</p>
            </div>
            <div className="rounded-xl border p-5 bg-gray-50">
              <h3 className="font-semibold text-lg mb-2">{t.faqItems[3].question}</h3>
              <p className="text-gray-600">{t.faqItems[3].answer}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8">
        <div className="container mx-auto px-4 text-center">
          <p className="text-gray-400">
            &copy; {new Date().getFullYear()} <a href="https://www.miwebvacacional.online" target="_blank" rel="noreferrer" className="text-white hover:text-gray-200 underline underline-offset-4">Mi Web Vacacional</a>. {t.footerRights} <a href="mailto:info@miwebvacacional.online" className="text-white hover:text-gray-200 underline underline-offset-4">{t.footerContact}</a>
          </p>
        </div>
      </footer>
    </div>
  );
};
